import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';

import { Checkbox } from 'shared/components/ihcl/checkbox';
import { Input } from 'shared/components/ihcl/input';
import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { MonthPicker } from 'shared/components/ihcl/month_picker_split_fields';
import { Center } from 'shared/components/ihcl/positioning';
import { Select } from 'shared/components/ihcl/select';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { degreeTypeOptions } from 'shared/constants/selectOptions';

const InputWrapper = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.unit24,
  paddingBottom: $theme.sizing.unit24,
}));

const RemoveButtonContent = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.unit8,
}));

const Divider = styled('hr', ({ $theme }) => ({
  borderColor: $theme.colors.grayLine,
  width: '100%',
  height: '1px',
  margin: '0',
}));

const normalizeEducationExperience = (experience) => {
  const dateParts = experience?.graduation_date?.split('-');
  const yearFromDate = dateParts?.[0];
  const monthFromDate = dateParts?.[1];

  return {
    ...experience,
    year: yearFromDate || String(experience?.year || ''),
    month: monthFromDate || String(experience?.month || ''),
    graduation_month:
      monthFromDate || String(experience?.graduation_month || ''),
    graduation_year: yearFromDate || String(experience?.graduation_year || ''),
  };
};

const validateEducationExperience = (educationExperience) => {
  const errors = {} as any;
  if (!educationExperience?.degree_type) {
    errors.degree_type = 'Required';
  }
  if (
    !['high_school_diploma', 'ged'].includes(
      educationExperience?.degree_type
    ) &&
    !educationExperience?.major
  ) {
    errors.major = 'Required';
  }
  if (!educationExperience?.institution_name) {
    errors.institution_name = 'Required';
  }
  return errors;
};

const EducationExperienceForm = ({
  educationExperience,
  setEducationExperience,
  removeEducationExperience,
  errors,
  setErrors,
  instanceIdPrefix,
  educationIndex,
}: EducationExperienceFormProps) => {
  const [techCertificationProgram, setTechCertificationProgram] = useState(
    educationExperience?.tech_certification_program
  );

  const updateTechCertificationProgram = (value) => {
    setEducationExperience({
      ...educationExperience,
      tech_certification_program: value,
    });

    setTechCertificationProgram(value);
  };

  return (
    <InputWrapper>
      <div>
        <Select
          instanceId={`${instanceIdPrefix}-degreeType`}
          placeholder="Please select a level"
          value={degreeTypeOptions.filter(
            (option) => option.value === educationExperience?.degree_type
          )}
          valueKey="value"
          options={degreeTypeOptions}
          clearable={false}
          onChange={(evt) => {
            const inputValue = evt.value.length > 0 ? evt.value[0].value : '';
            setEducationExperience({
              ...educationExperience,
              degree_type: inputValue,
            });

            if (errors?.degree_type) {
              const newErrors = { ...errors };
              delete newErrors.degree_type;
              setErrors(newErrors);
            }
          }}
          error={Boolean(errors?.degree_type)}
          caption={errors?.degree_type?.toString()}
        />
      </div>
      <div>
        <Input
          instanceId={`${instanceIdPrefix}-institution-name`}
          value={educationExperience?.institution_name}
          onChange={(e) => {
            setEducationExperience({
              ...educationExperience,
              institution_name: e.target.value,
            });

            if (errors?.institution_name) {
              const newErrors = { ...errors };
              delete newErrors.institution_name;
              setErrors(newErrors);
            }
          }}
          label="Institution name"
          error={Boolean(errors?.institution_name)}
          caption={errors?.institution_name}
        />
      </div>
      <div>
        <Input
          instanceId={`${instanceIdPrefix}-field-of-study`}
          value={educationExperience?.major}
          onChange={(e) => {
            setEducationExperience({
              ...educationExperience,
              major: e.target.value,
            });

            if (errors?.major) {
              const newErrors = { ...errors };
              delete newErrors.major;
              setErrors(newErrors);
            }
          }}
          label="Field of study"
          error={Boolean(errors?.major)}
          caption={errors?.major}
        />
      </div>
      <MonthPicker
        instanceId={`${instanceIdPrefix}-graduationDate`}
        yearLabel="Grad year"
        yearValue={educationExperience?.year}
        monthLabel="Month"
        monthValue={educationExperience?.month}
        onChange={({ month, year }) => {
          setEducationExperience({
            ...educationExperience,
            graduation_month: month,
            graduation_year: year,
          });
        }}
      />
      <div>
        <Checkbox
          checked={techCertificationProgram}
          onChange={(e) => updateTechCertificationProgram(e.target.checked)}
          instanceId={`${instanceIdPrefix}-tech-certification-program`}
        >
          I completed my certification program at this institution
        </Checkbox>
      </div>
      {educationIndex > 0 && (
        <Button
          variant="minimal"
          color="neutral"
          onClick={removeEducationExperience}
          data-testid={`${instanceIdPrefix}-remove-education-experience`}
        >
          <RemoveButtonContent>
            Remove education
            <LucideIcon name="Trash2" size="unit16" />
          </RemoveButtonContent>
        </Button>
      )}
      <Divider />
    </InputWrapper>
  );
};

type EducationExperience = {
  degree_type: string;
  institution_name: string;
  major: string;
  year: string;
  month: string;
  graduation_month: string;
  graduation_year: string;
  tech_certification_program: boolean;
};

type EducationExperienceFormProps = {
  educationExperience: EducationExperience;
  educationIndex: number;
  errors: EducationExperienceErrors;
  instanceIdPrefix: string;
  setEducationExperience: (educationExperience: EducationExperience) => void;
  removeEducationExperience: () => void;
  setErrors: (errors: EducationExperienceErrors) => void;
};

type EducationExperienceErrors = {
  degree_type?: string;
  institution_name?: string;
  major?: string;
};

const OnboardingTechCredentials = ({
  nextAction = null,
  onboardingWrapper = null,
  onboardingWrapperProps = {},
  previousAction = null,
  stepNumber,
}: OnboardingTechCredentialsProps) => {
  const { onboardingData, setOnboardingData } = useContext(OnboardingContext);

  const [educationExperiences, setEducationExperiences] = useState(
    onboardingData?.talentEducationExperiences?.length > 0
      ? onboardingData.talentEducationExperiences.map(
          normalizeEducationExperience
        )
      : [normalizeEducationExperience({})]
  );

  const [errors, setErrors] = useState<EducationExperienceErrors>({});

  useEffect(() => {
    if (
      JSON.stringify(educationExperiences) !==
      JSON.stringify(onboardingData.talentEducationExperiences)
    ) {
      setOnboardingData({
        ...onboardingData,
        talentEducationExperiences: educationExperiences,
      });
    }
  }, [educationExperiences, onboardingData, setOnboardingData]);

  if (!onboardingData || Object.keys(onboardingData).length < 1) {
    return null;
  }

  const addEducationExperience = () => {
    setEducationExperiences([
      ...educationExperiences,
      normalizeEducationExperience({}),
    ]);
  };

  const removeEducationExperience = (index) => {
    const newEducationExperiences = [...educationExperiences];
    newEducationExperiences.splice(index, 1);
    setEducationExperiences(newEducationExperiences);

    // Clean up errors for the removed experience
    const newErrors = { ...errors };
    delete newErrors[`educationExperience${index}`];
    // Renumber remaining errors
    const renumberedErrors = {};
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith('educationExperience')) {
        const num = parseInt(key.replace('educationExperience', ''), 10);
        if (num > index) {
          renumberedErrors[`educationExperience${num - 1}`] = newErrors[key];
        } else {
          renumberedErrors[key] = newErrors[key];
        }
      } else {
        renumberedErrors[key] = newErrors[key];
      }
    });
    setErrors(renumberedErrors);
  };

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={false}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['talentEducationExperiences']}
      stepName="Education / credentials"
      onboardingWrapper={onboardingWrapper}
      onboardingWrapperProps={onboardingWrapperProps}
      validateBeforeSubmit={() => {
        const validationErrors: EducationExperienceErrors = {};

        educationExperiences.forEach((experience, index) => {
          const experienceErrors = validateEducationExperience(experience);
          if (Object.keys(experienceErrors).length > 0) {
            validationErrors[`educationExperience${index}`] = experienceErrors;
          }
        });

        if (Object.keys(validationErrors).length) {
          setErrors(validationErrors);
          return false;
        }

        setErrors({});
        return true;
      }}
    >
      <Center>
        <OnboardingTitle>Where did you earn your credentials?</OnboardingTitle>
        <OnboardingSubtitle>
          Please add relevant educational certificates and degrees.
        </OnboardingSubtitle>

        {educationExperiences.map((educationExperience, index) => {
          const key = `education--${educationExperience.degree_type}-${index}`;

          return (
            <div key={key}>
              <EducationExperienceForm
                educationExperience={educationExperience}
                removeEducationExperience={() =>
                  removeEducationExperience(index)
                }
                educationIndex={index}
                setEducationExperience={(updatedExperience) => {
                  const newEducationExperiences = [...educationExperiences];
                  newEducationExperiences[index] = updatedExperience;
                  setEducationExperiences(newEducationExperiences);
                }}
                errors={errors[`educationExperience${index}`] || {}}
                setErrors={(newErrors) => {
                  const updatedErrors = { ...errors };
                  if (Object.keys(newErrors).length > 0) {
                    updatedErrors[`educationExperience${index}`] = newErrors;
                  } else {
                    delete updatedErrors[`educationExperience${index}`];
                  }
                  setErrors(updatedErrors);
                }}
                instanceIdPrefix={`educationExperience${index}`}
              />
            </div>
          );
        })}

        <Button variant="minimal" onClick={addEducationExperience}>
          + Add another educational experience
        </Button>
      </Center>
    </Onboarding>
  );
};

type OnboardingTechCredentialsProps = {
  nextAction?: string;
  onboardingWrapper?: React.ComponentType<
    PropsWithChildren<{
      $flushSides: boolean;
      $compactMaxWidth: boolean;
      $wrapperProps: any; // TODO fix this type
    }>
  >;
  onboardingWrapperProps?: Object;
  previousAction?: string;
  stepNumber: number;
};

export default OnboardingTechCredentials;
